import Toggle from '@app/components/inputs/Toggle';
import {
  SupplierValidationPayeeWithAccountsVerification,
  SupplierValidationVerificationRecordEvidence,
} from '@mortee/domain/validationSystem';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { FC } from 'react';
import {
  ClockIcon,
  DateInputContainer,
  InputContainer,
  InputGroup,
  Label,
  PrivatePayeeLabel,
  PrivatePayeeRow,
  ResetLink,
  RightAlignedGroup,
  SanctionsContentRow,
  SanctionsDateInputContainer,
  SanctionsHeaderRow,
  SanctionsSection,
  SaveButton,
  SecondRow,
  StyledDateInput,
  StyledInput,
  TopActions,
  TopRow,
  TopSection as TopSectionContainer,
} from '../NewVerificationTab.styles';
import { formatDate, parseDate } from '../NewVerificationTab.utils';
import { SanctionsScreeningResultStatus } from '@app/domain/validatedPayee';

interface TopSectionProps {
  verificationData: SupplierValidationPayeeWithAccountsVerification;
  knoxId: string;
  effectiveDate: number | undefined;
  privatePayee: boolean;
  sanctionsStatus: string | null;
  sanctionsScreeningDate: number | undefined;
  onKnoxIdChange: (value: string) => void;
  onEffectiveDateChange: (value: number | undefined) => void;
  onPrivatePayeeChange: (value: boolean) => void;
  onSanctionsStatusChange: (value: string) => void;
  onSanctionsScreeningDateChange: (value: number | undefined) => void;
  onResetSanctions: () => void;
  hasChanges: boolean;
  isSaving?: boolean;
  onSave: () => void;
  evidence: SupplierValidationVerificationRecordEvidence | undefined;
}

export const TopSection: FC<TopSectionProps> = ({
  knoxId,
  effectiveDate,
  privatePayee,
  sanctionsStatus,
  sanctionsScreeningDate,
  onKnoxIdChange,
  onEffectiveDateChange,
  onPrivatePayeeChange,
  onSanctionsStatusChange,
  onSanctionsScreeningDateChange,
  onResetSanctions,
  hasChanges,
  isSaving,
  onSave,
}) => {
  return (
    <TopSectionContainer>
      <TopRow>
        <InputGroup>
          <Label>Knox ID</Label>
          <InputContainer>
            <StyledInput
              name='knox-id'
              data-testid='knox-id'
              type='text'
              placeholder='Knox ID'
              value={knoxId}
              onChange={(e): void => onKnoxIdChange(e.target.value)}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <Label>Effective Date</Label>
          <DateInputContainer>
            <StyledDateInput
              type='text'
              value={formatDate(effectiveDate)}
              onChange={(e): void => {
                const parsed = parseDate(e.target.value);
                onEffectiveDateChange(parsed);
              }}
            />
            <ClockIcon>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <circle cx='12' cy='12' r='10' stroke='#666' strokeWidth='2' />
                <path d='M12 6v6l4 2' stroke='#666' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </ClockIcon>
          </DateInputContainer>
        </InputGroup>

        <RightAlignedGroup>
          <PrivatePayeeRow>
            <PrivatePayeeLabel>Private Payee</PrivatePayeeLabel>
            <Toggle
              accessibilityLabel='Private Payee toggle'
              name='private-payee'
              value={privatePayee}
              onChange={(value): void => onPrivatePayeeChange(value ?? false)}
            />
          </PrivatePayeeRow>
        </RightAlignedGroup>
      </TopRow>

      <SecondRow>
        <SanctionsSection>
          <SanctionsHeaderRow>
            <Label>Sanctions</Label>
            <ResetLink onClick={onResetSanctions}>Reset</ResetLink>
          </SanctionsHeaderRow>
          <SanctionsContentRow>
            <SanctionsDateInputContainer>
              <StyledDateInput
                id='sanctionsScreeningTimestamp'
                type='date'
                max={new Date().toISOString().split('T')[0]}
                value={sanctionsScreeningDate ? new Date(sanctionsScreeningDate).toISOString().split('T')[0] : ''}
                onChange={(e): void => {
                  const val = e.target.value;
                  onSanctionsScreeningDateChange(val ? new Date(val).getTime() : undefined);
                }}
              />
            </SanctionsDateInputContainer>
            <RadioGroup value={sanctionsStatus} onChange={(e): void => onSanctionsStatusChange(e.target.value)} row>
              <FormControlLabel
                value={SanctionsScreeningResultStatus.Passed}
                control={<Radio sx={{ color: '#1976d2' }} />}
                label='Screened'
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 510, lineHeight: '16px' } }}
              />
              <FormControlLabel
                value={SanctionsScreeningResultStatus.Rejected}
                control={<Radio sx={{ color: '#1976d2' }} />}
                label='Potential Matches'
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 510, lineHeight: '16px' } }}
              />
            </RadioGroup>
          </SanctionsContentRow>
        </SanctionsSection>
        <TopActions>
          <SaveButton disabled={!hasChanges || isSaving} onClick={onSave}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </SaveButton>
        </TopActions>
      </SecondRow>
    </TopSectionContainer>
  );
};
